// PROD-3310 billing bridge.
// Mirrors: dev-scrips-pm-react src/components/ui/button.tsx (the PM `Button` adapter, shadcn variant and size names).
// Wraps:   Signal DS `Button` (@scripsteam/scrips-signal-ds).
// Overrides: only the prop vocabulary. PM's variant names map onto the DS variants and PM's size names onto the DS
// size steps; the look PM's adapter merges in (Small 32 / px-3, Medium 40 / px-4, neutral Secondary, Ghost on the
// secondary ink, Danger fill, 50% disabled) is already applied to every DS Button by `src/styles/showcase-bridge.css`
// (`button[data-variant][data-size]`), the same designer spec. The icon-only squares PM keeps local (`icon`,
// `icon-xs`, `icon-sm`, `icon-lg`) have no DS step: they render on the DS `sm` step with `data-bridge-size`, and
// `src/styles/showcase-billing.css` draws the square. Size is NEVER passed through className: the DS Button merges
// className with tailwind-merge, which drops a size class when a colour class follows it.
import { Children, forwardRef, isValidElement, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Button, type ButtonSize, type ButtonVariant } from '@scripsteam/scrips-signal-ds';

export type BridgeButtonVariant = 'default' | 'outline' | 'secondary' | 'ghost' | 'destructive' | 'link';
export type BridgeButtonSize = 'default' | 'xs' | 'sm' | 'lg' | 'icon' | 'icon-xs' | 'icon-sm' | 'icon-lg';

/** PM variant name -> DS variant (PM `VARIANT_TO_DS`). */
export const BRIDGE_VARIANT_TO_DS: Readonly<Record<BridgeButtonVariant, ButtonVariant>> = Object.freeze({
  default: 'primary',
  outline: 'secondary',
  secondary: 'secondary',
  ghost: 'tertiary',
  destructive: 'destructive',
  link: 'link',
});

/** PM size name -> DS size step (PM `SIZE_TO_DS`; the icon squares sit on `sm` and are squared by the sheet). */
export const BRIDGE_SIZE_TO_DS: Readonly<Record<BridgeButtonSize, ButtonSize>> = Object.freeze({
  default: 'sm',
  xs: 'row',
  sm: 'sm',
  lg: 'md',
  icon: 'sm',
  'icon-xs': 'row',
  'icon-sm': 'sm',
  'icon-lg': 'md',
});

const ICON_SIZES: ReadonlySet<BridgeButtonSize> = new Set(['icon', 'icon-xs', 'icon-sm', 'icon-lg']);

export interface BridgeButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: BridgeButtonVariant;
  size?: BridgeButtonSize;
}

interface SplitChildren {
  icon?: ReactNode;
  iconPosition?: 'leading' | 'trailing';
  label: ReactNode;
}

/**
 * PM writes `<Button><Plus /> Add service</Button>` and relies on the button's flex gap between the glyph and the
 * label. The DS Button wraps ALL children in one label span, which loses that gap. A leading (or trailing) component
 * element that sits beside other content is therefore handed to the DS `icon` slot (16 square, `gap-2`) and the label
 * keeps the rest. A lone element (an icon-only button) and string-tagged elements (`<span>`, `<svg>`) stay in the label.
 */
export function splitIcon(children: ReactNode): SplitChildren {
  const parts = Children.toArray(children);
  if (parts.length < 2) return { label: children };
  const isIcon = (node: ReactNode) => isValidElement(node) && typeof node.type !== 'string';
  const first = parts[0];
  const last = parts[parts.length - 1];
  if (isIcon(first)) {
    const rest = parts.slice(1);
    if (typeof rest[0] === 'string') rest[0] = rest[0].replace(/^\s+/, '');
    return { icon: first, iconPosition: 'leading', label: rest };
  }
  if (isIcon(last)) {
    const rest = parts.slice(0, -1);
    const tail = rest.length - 1;
    if (typeof rest[tail] === 'string') rest[tail] = rest[tail].replace(/\s+$/, '');
    return { icon: last, iconPosition: 'trailing', label: rest };
  }
  return { label: children };
}

export const BridgeButton = forwardRef<HTMLButtonElement, BridgeButtonProps>(function BridgeButton(
  { variant = 'default', size = 'default', className, children, ...props },
  ref,
) {
  const { icon, iconPosition, label } = splitIcon(children);
  return (
    <Button
      ref={ref}
      data-slot="button"
      data-bridge="button"
      data-bridge-size={ICON_SIZES.has(size) ? size : undefined}
      variant={BRIDGE_VARIANT_TO_DS[variant]}
      size={BRIDGE_SIZE_TO_DS[size]}
      className={className}
      icon={icon}
      iconPosition={iconPosition}
      {...props}
    >
      {label}
    </Button>
  );
});
